/**
 * DailySalesReport.jsx — R1
 * Route: /pos/reports/daily-sales
 *
 * Response-aligned version.
 *
 * Important response mapping handled:
 * - Order number: display_order_no / order_number
 * - Row key: id / order_number
 * - Total bill count: totals.bill_count
 * - Grand total: totals.total_grand
 * - Row tax: row.tax
 * - Dynamic tax columns: applicableTaxes + row[taxName]
 * - Dynamic tax totals: totals[taxName]
 * - Table/Room display: room_number or table_id fallback
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table, Button, Popover, Spin, Tag, Empty } from "antd";
import {
  Download,
  Filter,
  ChevronLeft,
  ChevronRight,
  Calendar,
  RefreshCw,
  Book as Receipt,
  Search,
  X,
  DollarSign,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Printer,
} from "react-feather";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import PrintReportPreview from "../../reports/PrintReportPreview";
import PosModuleTopNav from "../../components/navigation/PosModuleTopNav";
import SelectComponent from "react-select";

const BUSINESS_DATE_KEYS = ["posbusiness_date", "business_date", "pos_business_date"];

const getToken = () => localStorage.getItem("token");

const getApiUrl = () => {
  const base = (process.env.REACT_APP_API_URL || "http://localhost:3000/api").replace(/\/+$/, "");
  return base.endsWith("/pos") ? base : `${base}/pos`;
};

const getPreviewApiUrl = () => {
  const base = (process.env.REACT_APP_API_URL || "http://localhost:3000/api").replace(/\/+$/, "");
  return base;
};

const getCurrency = () => localStorage.getItem("currencycode") || "₹";

const getBusinessDate = () => {
  for (const key of BUSINESS_DATE_KEYS) {
    const value = localStorage.getItem(key);
    if (value && moment(value).isValid()) return moment(value).startOf("day");
  }

  return moment().startOf("day");
};

const money2 = (value) => Number(value || 0).toFixed(2);

const asMoney = (value, currency = "₹") =>
  `${currency} ${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const shortId = (value) => {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= 12) return text;
  return `${text.slice(0, 8)}…${text.slice(-4)}`;
};

const getOrderNumber = (row) =>
  row?.display_order_no || row?.display_order_number || row?.order_number || row?.order_id || row?.id || "—";

const getTableRoom = (row) => {
  if (row?.room_number) return `Room ${row.room_number}`;
  if (row?.table_no) return `Table ${row.table_no}`;
  if (row?.table_number) return `Table ${row.table_number}`;
  if (row?.table_id) return `Table ${shortId(row.table_id)}`;
  if (row?.room_id) return `Room ${shortId(row.room_id)}`;
  return "—";
};

const getRowTax = (row) =>
  Number(row?.tax ?? row?.total_tax ?? row?.tax_amount ?? row?.total_tax_amount ?? 0);

const getGrandTotal = (row) =>
  Number(row?.grand_total ?? row?.total_amount ?? row?.total ?? 0);

const getDiscountAmount = (row) => {
  if (row?.discount_amount !== undefined) return Number(row.discount_amount || 0);
  if (row?.discount !== undefined) return Number(row.discount || 0);

  // API response currently sends discount_percentage only. If exact amount is not sent,
  // show 0 instead of guessing from subtotal/tax/service/delivery.
  return 0;
};

const getBillCount = (totals) =>
  Number(totals?.bill_count ?? totals?.total_bills ?? totals?.bills ?? 0);

const getTotalGrand = (totals) =>
  Number(totals?.total_grand ?? totals?.total_grand_total ?? totals?.grand_total ?? 0);

const getTotalDiscount = (totals) =>
  Number(totals?.total_discount ?? totals?.discount_total ?? 0);

const getTotalDelivery = (totals) =>
  Number(totals?.total_delivery ?? totals?.delivery_total ?? 0);

const csvEscape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const downloadCSV = (headers, data, filename) => {
  const safeHeaders = headers.filter((header) => header.key);
  const headerLine = safeHeaders.map((header) => csvEscape(header.label)).join(",");
  const rows = data.map((row) =>
    safeHeaders.map((header) => csvEscape(row[header.key])).join(",")
  );

  const blob = new Blob([[headerLine, ...rows].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 14,
    borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.12)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(37, 99, 235, 0.12)" : "none",
    fontSize: 13,
    fontWeight: 700,
    "&:hover": {
      borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.22)",
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: "#94a3b8",
    fontWeight: 700,
  }),
  singleValue: (base) => ({
    ...base,
    color: "#0f172a",
    fontWeight: 850,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 2147483647,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 2147483647,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 18px 42px rgba(15, 23, 42, 0.18)",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    fontWeight: state.isSelected ? 850 : 700,
    cursor: "pointer",
  }),
};

const DailySalesReport = () => {
  const currency = getCurrency();
  const apiURL = getApiUrl();
  const previewApiURL = getPreviewApiUrl();

  const [singleDate, setSingleDate] = useState(getBusinessDate());
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [totals, setTotals] = useState({});
  const [applicableTaxes, setApplicableTaxes] = useState([]);
  const [filterVisible, setFilterVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [filters, setFilters] = useState({
    order_type: null,
    is_complementary: null,
    is_settled: null,
    payment_method: null,
  });

  const orderTypeOptions = [
    "Dine In",
    "Table Service",
    "Room Service",
    "Delivery",
    "Takeaway",
    "Counter",
  ].map((value) => ({ value, label: value }));

  const paymentMethodOptions = [
    "CASH",
    "Cash",
    "UPI",
    "CARD",
    "Card",
    "BANK_TRANSFER",
    "Room Credit",
    "Partial",
  ].map((value) => ({ value, label: value }));

  const boolOptions = [
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
  ];

  const fetchReport = useCallback(async () => {
    const token = getToken();

    if (!token) {
      Swal.fire({
        icon: "error",
        title: "Authentication required",
        text: "Login token is missing. Please login again.",
      });
      return;
    }

    setLoading(true);

    try {
      const params = {
        business_date: singleDate.format("YYYY-MM-DD"),
        ...(filters.order_type && { order_type: filters.order_type.value }),
        ...(filters.is_complementary && { is_complementary: filters.is_complementary.value }),
        ...(filters.is_settled && { is_settled: filters.is_settled.value }),
        ...(filters.payment_method && { payment_method: filters.payment_method.value }),
      };

      const res = await axios.get(`${apiURL}/daily-sales`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      if (res.data?.success) {
        setOrders(Array.isArray(res.data.orders) ? res.data.orders : []);
        setTotals(res.data.totals || {});
        setApplicableTaxes(Array.isArray(res.data.applicableTaxes) ? res.data.applicableTaxes : []);
      } else {
        setOrders([]);
        setTotals({});
        setApplicableTaxes([]);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.error || err.message || "Failed to load daily sales report.",
      });
    } finally {
      setLoading(false);
    }
  }, [singleDate, filters, apiURL]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleDateNav = (dir) => {
    setSingleDate((previous) =>
      previous.clone().add(dir === "next" ? 1 : -1, "days").startOf("day")
    );
  };

  const resetToBusinessDate = () => {
    setSingleDate(getBusinessDate());
  };

  const filteredOrders = useMemo(() => {
    const query = String(searchText || "").trim().toLowerCase();
    if (!query) return orders;

    return orders.filter((row) =>
      [
        getOrderNumber(row),
        row.order_number,
        row.display_order_no,
        row.order_type,
        row.source,
        getTableRoom(row),
        row.payment_method,
        row.status,
        row.created_by,
      ]
        .map((value) => String(value || "").toLowerCase())
        .some((value) => value.includes(query))
    );
  }, [orders, searchText]);

  const derivedTotals = useMemo(() => {
    const billCount = getBillCount(totals) || filteredOrders.length;
    const grand = getTotalGrand(totals) || filteredOrders.reduce((sum, row) => sum + getGrandTotal(row), 0);
    const tax = Number(totals?.total_tax ?? 0) || filteredOrders.reduce((sum, row) => sum + getRowTax(row), 0);
    const subtotal =
      Number(totals?.total_subtotal ?? 0) ||
      filteredOrders.reduce((sum, row) => sum + Number(row.subtotal || 0), 0);

    return {
      billCount,
      subtotal,
      tax,
      discount: getTotalDiscount(totals),
      delivery: getTotalDelivery(totals),
      grand,
    };
  }, [totals, filteredOrders]);

  const exportRows = useMemo(
    () =>
      filteredOrders.map((row) => {
        const base = {
          order_no: getOrderNumber(row),
          order_time: row.order_time || "",
          order_type: row.order_type || "",
          source: row.source || "",
          table_room: getTableRoom(row),
          subtotal: money2(row.subtotal),
          total_tax: money2(getRowTax(row)),
          discount_percentage: `${Number(row.discount_percentage || 0).toFixed(1)}%`,
          discount_amount: money2(getDiscountAmount(row)),
          delivery_charge: money2(row.delivery_charge),
          grand_total: money2(getGrandTotal(row)),
          payment_method: row.payment_method || "",
          is_settled: row.is_settled ? "Yes" : "No",
          status: row.status || "",
          is_complementary: row.is_complementary ? "Yes" : "No",
          billing_type: row.billing_type || "",
          created_by: row.created_by || "",
        };

        applicableTaxes.forEach((taxName) => {
          base[taxName] = money2(row[taxName]);
        });

        return base;
      }),
    [filteredOrders, applicableTaxes]
  );

  const exportHeaderList = useMemo(() => {
    const baseHeaders = [
      { label: "Order No", key: "order_no" },
      { label: "Time", key: "order_time" },
      { label: "Order Type", key: "order_type" },
      { label: "Source", key: "source" },
      { label: "Table / Room", key: "table_room" },
      { label: `Subtotal (${currency})`, key: "subtotal" },
    ];

    const taxHeaders = applicableTaxes.map((taxName) => ({
      label: `${taxName} (${currency})`,
      key: taxName,
    }));

    return [
      ...baseHeaders,
      ...taxHeaders,
      { label: `Total Tax (${currency})`, key: "total_tax" },
      { label: "Disc %", key: "discount_percentage" },
      { label: `Discount (${currency})`, key: "discount_amount" },
      { label: `Delivery (${currency})`, key: "delivery_charge" },
      { label: `Grand Total (${currency})`, key: "grand_total" },
      { label: "Payment", key: "payment_method" },
      { label: "Settled", key: "is_settled" },
      { label: "Status", key: "status" },
      { label: "Comp", key: "is_complementary" },
      { label: "Billing Type", key: "billing_type" },
      { label: "Created By", key: "created_by" },
    ];
  }, [applicableTaxes, currency]);

  const staticColumns = [
    {
      title: "Order No",
      dataIndex: "order_number",
      width: 140,
      fixed: "left",
      render: (_, row) => (
        <div className="ns-dsr-order-cell">
          <strong>{getOrderNumber(row)}</strong>
          {row.is_complementary ? <span className="ns-dsr-mini-badge comp">Comp</span> : null}
        </div>
      ),
    },
    {
      title: "Time",
      dataIndex: "order_time",
      width: 85,
      render: (value) => <span className="ns-dsr-muted-strong">{value || "—"}</span>,
    },
    {
      title: "Order Type",
      dataIndex: "order_type",
      width: 125,
      render: (value) => <span className="ns-dsr-soft-pill">{value || "—"}</span>,
    },
    {
      title: "Source",
      dataIndex: "source",
      width: 115,
      render: (value) => value || "—",
    },
    {
      title: "Table / Room",
      dataIndex: "table_room",
      width: 135,
      render: (_, row) => <span className="ns-dsr-main-text">{getTableRoom(row)}</span>,
    },
    {
      title: `Subtotal (${currency})`,
      dataIndex: "subtotal",
      width: 120,
      align: "right",
      render: (value) => money2(value),
    },
  ];

  const taxColumns = applicableTaxes.map((taxName) => ({
    title: `${taxName} (${currency})`,
    dataIndex: taxName,
    width: 115,
    align: "right",
    render: (value) => money2(value),
  }));

  const trailingColumns = [
    {
      title: `Total Tax (${currency})`,
      dataIndex: "tax",
      width: 120,
      align: "right",
      render: (_, row) => money2(getRowTax(row)),
    },
    {
      title: "Disc %",
      dataIndex: "discount_percentage",
      width: 80,
      align: "right",
      render: (value) => `${Number(value || 0).toFixed(1)}%`,
    },
    {
      title: `Discount (${currency})`,
      dataIndex: "discount_amount",
      width: 115,
      align: "right",
      render: (_, row) => money2(getDiscountAmount(row)),
    },
    {
      title: `Delivery (${currency})`,
      dataIndex: "delivery_charge",
      width: 115,
      align: "right",
      render: (value) => money2(value),
    },
    {
      title: `Grand Total (${currency})`,
      dataIndex: "grand_total",
      width: 130,
      align: "right",
      fixed: "right",
      render: (_, row) => <strong className="ns-dsr-grand">{money2(getGrandTotal(row))}</strong>,
    },
    {
      title: "Payment",
      dataIndex: "payment_method",
      width: 115,
      render: (value) => <span className="ns-dsr-soft-pill">{value || "—"}</span>,
    },
    {
      title: "Settled",
      dataIndex: "is_settled",
      width: 90,
      align: "center",
      render: (value) => (
        <Tag color={value ? "green" : "orange"}>{value ? "Yes" : "No"}</Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 100,
      render: (value) => value || "—",
    },
    {
      title: "Comp",
      dataIndex: "is_complementary",
      width: 75,
      align: "center",
      render: (value) => (value ? <Tag color="blue">Yes</Tag> : <span className="ns-dsr-muted">—</span>),
    },
  ];

  const columns = [...staticColumns, ...taxColumns, ...trailingColumns];

  const handleDownloadCSV = () => {
    downloadCSV(
      exportHeaderList,
      exportRows,
      `Daily_Sales_${singleDate.format("YYYY-MM-DD")}.csv`
    );
  };

  const handlePrint = () => {
    setExportData(exportRows);
    setExportHeaders(exportHeaderList);
    setPreviewVisible(true);
  };

  const clearFilters = () => {
    setFilters({
      order_type: null,
      is_complementary: null,
      is_settled: null,
      payment_method: null,
    });
    setSearchText("");
  };

  const filterContent = (
    <div className="ns-dsr-filter-panel">
      {[
        { label: "Order Type", key: "order_type", options: orderTypeOptions },
        { label: "Complementary", key: "is_complementary", options: boolOptions },
        { label: "Settled", key: "is_settled", options: boolOptions },
        { label: "Payment Method", key: "payment_method", options: paymentMethodOptions },
      ].map((field) => (
        <div key={field.key}>
          <label>{field.label}</label>
          <SelectComponent
            isClearable
            options={field.options}
            value={filters[field.key]}
            onChange={(value) =>
              setFilters((previous) => ({ ...previous, [field.key]: value }))
            }
            styles={selectStyles}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
            menuPlacement="auto"
            menuShouldScrollIntoView={false}
          />
        </div>
      ))}

      <div className="ns-dsr-filter-actions">
        <Button onClick={clearFilters}>Clear</Button>
        <Button
          type="primary"
          onClick={() => {
            setFilterVisible(false);
            fetchReport();
          }}
        >
          Apply
        </Button>
      </div>
    </div>
  );

  const summaryRow = () => (
    <Table.Summary.Row className="ns-dsr-summary-row">
      <Table.Summary.Cell index={0} colSpan={5}>
        TOTAL — {derivedTotals.billCount} bill{derivedTotals.billCount === 1 ? "" : "s"}
      </Table.Summary.Cell>

      <Table.Summary.Cell index={5} align="right">
        {asMoney(derivedTotals.subtotal, currency)}
      </Table.Summary.Cell>

      {applicableTaxes.map((taxName, index) => (
        <Table.Summary.Cell key={taxName} index={6 + index} align="right">
          {asMoney(totals?.[taxName] || 0, currency)}
        </Table.Summary.Cell>
      ))}

      <Table.Summary.Cell align="right">{asMoney(derivedTotals.tax, currency)}</Table.Summary.Cell>
      <Table.Summary.Cell align="right">—</Table.Summary.Cell>
      <Table.Summary.Cell align="right">{asMoney(derivedTotals.discount, currency)}</Table.Summary.Cell>
      <Table.Summary.Cell align="right">{asMoney(derivedTotals.delivery, currency)}</Table.Summary.Cell>
      <Table.Summary.Cell align="right">{asMoney(derivedTotals.grand, currency)}</Table.Summary.Cell>
      <Table.Summary.Cell colSpan={4} />
    </Table.Summary.Row>
  );

  const footerData = [
    { key: "Bills", value: derivedTotals.billCount },
    { key: "Subtotal", value: asMoney(derivedTotals.subtotal, currency) },
    { key: "Tax", value: asMoney(derivedTotals.tax, currency) },
    { key: "Grand Total", value: asMoney(derivedTotals.grand, currency) },
  ];

  return (
    <div className="page-wrapper">
      <style>{`
        .ns-dsr-page {
          min-height: calc(100vh - 70px);
          background:
            radial-gradient(circle at 10% 4%, rgba(37, 99, 235, 0.10), transparent 28%),
            radial-gradient(circle at 86% 0%, rgba(20, 184, 166, 0.12), transparent 26%),
            #f8fafc;
          padding-bottom: 26px;
        }

        .ns-dsr-hero {
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.16), transparent 34%),
            radial-gradient(circle at top right, rgba(20, 184, 166, 0.10), transparent 30%),
            linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 24px;
          box-shadow: 0 18px 48px rgba(15, 23, 42, 0.08);
          padding: 16px 18px;
          margin-bottom: 14px;
          display: flex;
          justify-content: space-between;
          gap: 16px;
          align-items: flex-start;
          flex-wrap: wrap;
        }

        .ns-dsr-title-wrap {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          min-width: 0;
        }

        .ns-dsr-title-icon {
          width: 46px;
          height: 46px;
          border-radius: 17px;
          background: linear-gradient(135deg, #1d4ed8, #38bdf8);
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 14px 30px rgba(37, 99, 235, 0.22);
          flex: 0 0 auto;
        }

        .ns-dsr-eyebrow {
          color: #2563eb;
          font-size: 0.7rem;
          font-weight: 950;
          letter-spacing: 0.13em;
          text-transform: uppercase;
          margin-bottom: 4px;
        }

        .ns-dsr-title {
          color: #001f5b;
          font-size: 1.38rem;
          line-height: 1.16;
          font-weight: 950;
          margin: 0;
        }

        .ns-dsr-subtitle {
          color: #64748b;
          font-size: 0.84rem;
          line-height: 1.5;
          margin: 6px 0 0;
          max-width: 720px;
        }

        .ns-dsr-date-switcher {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
          padding: 7px;
        }

        .ns-dsr-date-btn,
        .ns-dsr-reset-btn {
          height: 40px !important;
          border-radius: 14px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 7px !important;
          font-weight: 900 !important;
        }

        .ns-dsr-current-date {
          height: 40px;
          border-radius: 14px;
          background: #f8fafc;
          border: 1px solid rgba(15, 23, 42, 0.08);
          color: #0f172a;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 0.86rem;
          font-weight: 950;
          padding: 0 12px;
          white-space: nowrap;
        }

        .ns-dsr-kpi-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 10px;
          margin-bottom: 14px;
        }

        .ns-dsr-kpi-card {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
          padding: 13px;
          min-width: 0;
        }

        .ns-dsr-kpi-card span {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #64748b;
          font-size: 0.68rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-weight: 950;
          margin-bottom: 5px;
        }

        .ns-dsr-kpi-card strong {
          display: block;
          color: #0f172a;
          font-size: 1.16rem;
          font-weight: 950;
          line-height: 1.1;
        }

        .ns-dsr-kpi-card small {
          color: #94a3b8;
          font-size: 0.72rem;
          display: block;
          margin-top: 5px;
        }

        .ns-dsr-kpi-card.blue { border-top: 3px solid #2563eb; }
        .ns-dsr-kpi-card.green { border-top: 3px solid #22c55e; }
        .ns-dsr-kpi-card.amber { border-top: 3px solid #f59e0b; }
        .ns-dsr-kpi-card.slate { border-top: 3px solid #64748b; }

        .ns-dsr-shell {
          background: rgba(255, 255, 255, 0.98);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 24px;
          box-shadow: 0 18px 48px rgba(15, 23, 42, 0.08);
          overflow: hidden;
        }

        .ns-dsr-toolbar {
          padding: 14px 16px;
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          display: flex;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
          background: #ffffff;
        }

        .ns-dsr-search {
          position: relative;
          flex: 1 1 340px;
          max-width: 520px;
        }

        .ns-dsr-search svg {
          position: absolute;
          left: 13px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          pointer-events: none;
        }

        .ns-dsr-search input {
          width: 100%;
          height: 42px;
          border-radius: 14px;
          border: 1px solid rgba(15, 23, 42, 0.12);
          background: #ffffff;
          padding: 0 42px 0 40px;
          color: #0f172a;
          font-size: 0.84rem;
          font-weight: 780;
          outline: none;
          box-shadow: 0 6px 14px rgba(15, 23, 42, 0.04);
          transition: border-color 0.16s ease, box-shadow 0.16s ease;
        }

        .ns-dsr-search input:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }

        .ns-dsr-search button {
          position: absolute;
          right: 7px;
          top: 50%;
          transform: translateY(-50%);
          width: 28px;
          height: 28px;
          border-radius: 10px;
          border: 0;
          background: #f1f5f9;
          color: #64748b;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .ns-dsr-toolbar-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .ns-dsr-icon-btn {
          height: 40px !important;
          border-radius: 14px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 7px !important;
          font-weight: 900 !important;
        }

        .ns-dsr-table-wrap {
          padding: 16px;
        }

        .ns-dsr-table .ant-table {
          border-radius: 18px !important;
          overflow: hidden;
          border: 1px solid rgba(15, 23, 42, 0.08);
        }

        .ns-dsr-table .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #0f172a !important;
          font-weight: 950 !important;
          font-size: 0.78rem;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }

        .ns-dsr-table .ant-table-tbody > tr:hover > td {
          background: #f8fbff !important;
        }

        .ns-dsr-summary-row {
          background: #f0f9ff !important;
          font-weight: 950;
        }

        .ns-dsr-order-cell {
          display: flex;
          align-items: center;
          gap: 7px;
          flex-wrap: wrap;
        }

        .ns-dsr-order-cell strong,
        .ns-dsr-main-text {
          color: #0f172a;
          font-weight: 900;
        }

        .ns-dsr-grand {
          color: #047857;
          font-weight: 950;
        }

        .ns-dsr-muted,
        .ns-dsr-muted-strong {
          color: #94a3b8;
        }

        .ns-dsr-muted-strong {
          font-weight: 850;
        }

        .ns-dsr-soft-pill,
        .ns-dsr-mini-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          font-size: 0.74rem;
          font-weight: 950;
          white-space: nowrap;
        }

        .ns-dsr-soft-pill {
          background: #f8fafc;
          color: #475569;
          border: 1px solid rgba(15, 23, 42, 0.08);
          padding: 5px 9px;
        }

        .ns-dsr-mini-badge {
          padding: 4px 8px;
        }

        .ns-dsr-mini-badge.comp {
          background: #eff6ff;
          color: #1d4ed8;
          border: 1px solid #bfdbfe;
        }

        .ns-dsr-filter-panel {
          width: 285px;
          display: grid;
          gap: 13px;
        }

        .ns-dsr-filter-panel label {
          color: #0f172a;
          font-size: 0.78rem;
          font-weight: 900;
          display: block;
          margin-bottom: 5px;
        }

        .ns-dsr-filter-actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }

        .ns-dsr-loader {
          min-height: 420px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .ns-dsr-loader-card {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 22px;
          box-shadow: 0 18px 48px rgba(15, 23, 42, 0.10);
          padding: 26px 32px;
          display: inline-flex;
          gap: 14px;
          align-items: center;
          color: #64748b;
          font-weight: 900;
        }


        .ns-dsr-hero {
          display: grid !important;
          grid-template-columns: minmax(260px, 0.72fr) minmax(520px, 1.58fr) !important;
          align-items: start !important;
          flex-wrap: unset !important;
          padding: 16px 18px !important;
        }

        .ns-dsr-title-wrap {
          min-width: 0;
        }

        .ns-dsr-title {
          margin-bottom: 0 !important;
        }

        .ns-dsr-subtitle {
          display: none !important;
        }

        .ns-dsr-hero-right {
          min-width: 0;
          width: 100%;
          display: grid;
          gap: 8px;
          justify-items: end;
        }

        .ns-dsr-quicknav-inline {
          min-width: 0;
          width: 100%;
          display: flex;
          justify-content: flex-end;
        }

        .ns-dsr-date-switcher {
          justify-self: end;
        }

        .ns-dsr-toolbar {
          padding: 12px 14px !important;
        }

        .ns-dsr-kpi-grid {
          margin-bottom: 12px !important;
        }

        @media (max-width: 1280px) {
          .ns-dsr-hero {
            grid-template-columns: 1fr !important;
          }

          .ns-dsr-hero-right {
            justify-items: stretch;
          }

          .ns-dsr-quicknav-inline {
            justify-content: flex-start;
          }

          .ns-dsr-date-switcher {
            justify-self: start;
          }
        }

        @media (max-width: 1100px) {
          .ns-dsr-kpi-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 760px) {
          .ns-dsr-hero {
            border-radius: 22px;
            padding: 14px;
          }

          .ns-dsr-date-switcher {
            width: 100%;
            justify-content: space-between;
          }

          .ns-dsr-date-switcher .ant-btn {
            padding-left: 10px !important;
            padding-right: 10px !important;
          }

          .ns-dsr-kpi-grid {
            grid-template-columns: 1fr;
          }

          .ns-dsr-search {
            flex-basis: 100%;
            max-width: 100%;
          }
        }
      `}</style>

      <div className="content container-fluid ns-dsr-page">
        <section className="ns-dsr-hero">
          <div className="ns-dsr-title-wrap">
            <span className="ns-dsr-title-icon">
              <Receipt size={24} />
            </span>

            <div>
              <div className="ns-dsr-eyebrow">POS Revenue Report</div>
              <h2 className="ns-dsr-title">Daily Sales Report</h2>
            </div>
          </div>

          <div className="ns-dsr-hero-right">
            <div className="ns-dsr-quicknav-inline">
              <PosModuleTopNav activeKey="daily-sales" />
            </div>

            <div className="ns-dsr-date-switcher">
              <Button
              className="ns-dsr-date-btn"
              onClick={() => handleDateNav("prev")}
              aria-label="Previous business date"
            >
              <ChevronLeft size={16} />
            </Button>

            <div className="ns-dsr-current-date">
              <Calendar size={16} />
              {singleDate.format("DD MMM YYYY")}
            </div>

            <Button
              className="ns-dsr-date-btn"
              onClick={() => handleDateNav("next")}
              aria-label="Next business date"
            >
              <ChevronRight size={16} />
            </Button>

              <Button className="ns-dsr-reset-btn" onClick={resetToBusinessDate}>
                <RefreshCw size={14} />
                Business Date
              </Button>
            </div>
          </div>
        </section>

        <div className="ns-dsr-kpi-grid">
          <div className="ns-dsr-kpi-card blue">
            <span>
              <Receipt size={14} />
              Bills
            </span>
            <strong>{derivedTotals.billCount}</strong>
            <small>Orders in selected business date</small>
          </div>

          <div className="ns-dsr-kpi-card green">
            <span>
              <DollarSign size={14} />
              Revenue
            </span>
            <strong>{asMoney(derivedTotals.grand, currency)}</strong>
            <small>Grand total collection</small>
          </div>

          <div className="ns-dsr-kpi-card amber">
            <span>
              <CheckCircle size={14} />
              Tax
            </span>
            <strong>{asMoney(derivedTotals.tax, currency)}</strong>
            <small>Dynamic tax breakup enabled</small>
          </div>

          <div className="ns-dsr-kpi-card slate">
            <span>
              <CreditCard size={14} />
              Subtotal
            </span>
            <strong>{asMoney(derivedTotals.subtotal, currency)}</strong>
            <small>Before tax and final adjustments</small>
          </div>
        </div>

        <section className="ns-dsr-shell">
          <div className="ns-dsr-toolbar">
            <div className="ns-dsr-search">
              <Search size={17} />
              <input
                type="text"
                placeholder="Search order no, source, table, payment..."
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
              {searchText ? (
                <button type="button" onClick={() => setSearchText("")} aria-label="Clear search">
                  <X size={14} />
                </button>
              ) : null}
            </div>

            <div className="ns-dsr-toolbar-actions">
              <Button className="ns-dsr-icon-btn" onClick={fetchReport} loading={loading}>
                <RefreshCw size={15} />
                Refresh
              </Button>

              <Popover
                content={filterContent}
                title="Filters"
                trigger="click"
                open={filterVisible}
                onOpenChange={setFilterVisible}
                placement="bottomRight"
              >
                <Button className="ns-dsr-icon-btn">
                  <Filter size={15} />
                  Filter
                </Button>
              </Popover>

              <Popover
                placement="bottomRight"
                trigger="click"
                content={
                  <div style={{ display: "grid", gap: 8, minWidth: 210 }}>
                    <Button onClick={handleDownloadCSV}>
                      <Download size={15} />
                      Download CSV
                    </Button>
                    <Button onClick={handlePrint}>
                      <Printer size={15} />
                      Print / PDF Preview
                    </Button>
                  </div>
                }
              >
                <Button type="primary" className="ns-dsr-icon-btn">
                  <Download size={15} />
                  Download
                </Button>
              </Popover>
            </div>
          </div>

          <div className="ns-dsr-table-wrap">
            {loading ? (
              <div className="ns-dsr-loader">
                <div className="ns-dsr-loader-card">
                  <Spin size="large" />
                  <span>Loading daily sales report...</span>
                </div>
              </div>
            ) : filteredOrders.length === 0 ? (
              <Empty
                style={{ padding: "56px 0" }}
                description="No sales found for the selected business date or filters."
              />
            ) : (
              <Table
                columns={columns}
                dataSource={filteredOrders}
                rowKey={(row, index) => row.id || row.order_number || row.display_order_no || index}
                scroll={{ x: 1550, y: 540 }}
                size="small"
                bordered
                className="ns-dsr-table"
                pagination={{
                  pageSize: 50,
                  showSizeChanger: true,
                  pageSizeOptions: ["25", "50", "100", "200"],
                  showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} records`,
                }}
                summary={filteredOrders.length > 0 ? summaryRow : undefined}
              />
            )}
          </div>
        </section>

        <PrintReportPreview
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
          reportTitle={`Daily Sales Report — ${singleDate.format("DD MMM YYYY")}`}
          propertyName=""
          data={exportData}
          headers={exportHeaders}
          footerData={footerData}
          totalColumns={[]}
          apiEndpoint="transactions/generate-pdf"
          apiUrl={previewApiURL}
        />
      </div>
    </div>
  );
};

export default DailySalesReport;
